import React, { useState } from 'react';
import {
  Form,
  Input,
  DatePicker,
  Button,
  Table,
  Space,
  message,
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  SendOutlined,
  FileDoneOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/vi_VN';
import { prescriptionService } from '../../services';
import { Prescription } from '../../types';
import { formatDate } from '../../utils/format';
import PrescriptionDetailModal from '../../components/PrescriptionDetailModal';
import './PrescriptionManagementPage.css';

const PrescriptionManagementPage: React.FC = () => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedPrescriptionCode, setSelectedPrescriptionCode] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState({
    code: '',
    patientName: '',
    doctorName: '',
    startDate: '',
    endDate: '',
  });

  const { data: prescriptions, isLoading } = useQuery({
    queryKey: ['prescriptions', filters],
    queryFn: () => prescriptionService.getList(filters),
  });

  const sendMutation = useMutation({
    mutationFn: (ids: string[]) => prescriptionService.sendToNational(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prescriptions'] });
      message.success('Gửi đơn thuốc thành công!');
      setSelectedRowKeys([]);
    },
  });

  const exportMutation = useMutation({
    mutationFn: (ids: string[]) => prescriptionService.exportInvoice(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prescriptions'] });
      message.success('Xuất hóa đơn thành công!');
      setSelectedRowKeys([]);
    },
  });

  const onSearch = (values: any) => {
    setFilters({
      code: values.code || '',
      patientName: values.patientName || '',
      doctorName: values.doctorName || '',
      startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : '',
      endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : '',
    });
  };

  const handleViewDetail = (code: string) => {
    setSelectedPrescriptionCode(code);
    setDetailModalVisible(true);
  };

  const handleSendAll = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Vui lòng chọn ít nhất một đơn thuốc!');
      return;
    }
    sendMutation.mutate(selectedRowKeys as string[]);
  };

  const handleExportAll = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Vui lòng chọn ít nhất một đơn thuốc!');
      return;
    }
    exportMutation.mutate(selectedRowKeys as string[]);
  };

  const handleSendSingle = (id: string) => {
    sendMutation.mutate([id]);
  };

  const handleExportSingle = (id: string) => {
    exportMutation.mutate([id]);
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'index',
      width: 60,
    },
    {
      title: 'Mã đơn thuốc',
      dataIndex: 'code',
      key: 'code',
      render: (text: string, record: Prescription) => (
        <Button
          type="link"
          onClick={() => handleViewDetail(text)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Bác sĩ',
      dataIndex: 'doctorName',
      key: 'doctorName',
    },
    {
      title: 'Tên bệnh nhân',
      dataIndex: 'patientName',
      key: 'patientName',
    },
    {
      title: 'Chẩn đoán',
      dataIndex: 'diagnosis',
      key: 'diagnosis',
    },
    {
      title: 'Ngày tạo đơn',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Gửi đơn thuốc',
      key: 'sendStatus',
      render: (_: any, record: Prescription) => (
        <div>
          {record.status === 'sent' ? (
            <Space>
              <EyeOutlined style={{ color: '#385da4' }} />
              <span>Đã gửi ĐTQG</span>
            </Space>
          ) : (
            <Button
              type="link"
              icon={<SendOutlined />}
              onClick={() => handleSendSingle(record.id)}
              style={{ color: '#52c41a' }}
            >
              Gửi ĐTQG
            </Button>
          )}
        </div>
      ),
    },
    {
      title: 'Xuất hóa đơn',
      key: 'invoiceStatus',
      render: (_: any, record: Prescription) => (
        <div>
          {record.invoiceStatus === 'exported' ? (
            <Space>
              <EyeOutlined style={{ color: '#385da4' }} />
              <span>Đã xuất HĐ</span>
            </Space>
          ) : (
            <Button
              type="link"
              icon={<SendOutlined />}
              onClick={() => handleExportSingle(record.id)}
              style={{ color: '#52c41a' }}
            >
              Xuất HĐ
            </Button>
          )}
        </div>
      ),
    },
    {
      title: 'Tùy chọn',
      key: 'actions',
      render: (_: any, record: Prescription) => (
        <Space>
          {record.status === 'sent' && record.invoiceStatus === 'exported' ? (
            <>
              <Button type="link" icon={<DownloadOutlined />} />
              <Button type="link" icon={<MailOutlined />} />
            </>
          ) : (
            <>
              <Button type="link" icon={<EditOutlined />} />
              <Button type="link" danger icon={<DeleteOutlined />} />
            </>
          )}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => {
      setSelectedRowKeys(keys);
    },
  };

  const tableData = prescriptions?.map((p, index) => ({
    ...p,
    index: index + 1,
    key: p.id,
  }));

  return (
    <div className="prescription-management-page">
      <h1 className="page-title">Quản lý đơn thuốc ngày {formatDate(dayjs().format('YYYY-MM-DD'))}</h1>

      {/* <Card className="search-card"> */}
        <Form form={form} layout="inline" onFinish={onSearch} className="search-form">
          <Form.Item name="code">
            <Input placeholder="Mã đơn thuốc" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="patientName">
            <Input placeholder="Tên bệnh nhân" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="doctorName">
            <Input placeholder="Bác sĩ" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="startDate">
            <DatePicker placeholder="Từ ngày" format="DD/MM/YYYY" locale={locale} />
          </Form.Item>
          <Form.Item name="endDate">
            <DatePicker placeholder="Đến ngày" format="DD/MM/YYYY" locale={locale} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
              Tìm kiếm
            </Button>
          </Form.Item>
        </Form>
        <Space className="action-buttons">
          <Button type="primary" icon={<PlusOutlined />}>
            Import dữ liệu excel
          </Button>
          <Button type="primary" icon={<PlusOutlined />}>
            Nhập đơn thuốc thủ công
          </Button>
        </Space>
      {/* </Card> */}

      {/* <Card> */}
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={tableData}
          loading={isLoading}
          pagination={{ pageSize: 10 }}
        />
      {/* </Card> */}

      <div className="bulk-actions">
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSendAll}
          loading={sendMutation.isPending}
        >
          Gửi toàn bộ ĐTQG
        </Button>
        <Button
          type="primary"
          icon={<FileDoneOutlined />}
          onClick={handleExportAll}
          loading={exportMutation.isPending}
        >
          Xuất toàn bộ Hóa đơn
        </Button>
      </div>

      <PrescriptionDetailModal
        visible={detailModalVisible}
        code={selectedPrescriptionCode}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedPrescriptionCode(null);
        }}
      />
    </div>
  );
};

export default PrescriptionManagementPage;

